import React, { useState, useEffect, useCallback } from "react";
import { Save, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Card } from "./ui/card";
import { rbacService } from "../services/rbacService";
import toast from "react-hot-toast";
import TableSkeleton from "./skeletons/TableSkeleton";

import {
  Box,
  Typography,
  Switch,
  Tooltip,
  Stack,
  Divider,
} from "@mui/material";

import {
  Folder,
  Flag,
  ListChecks,
  Paperclip,
  PlayCircle,
  BarChart3,
  LayoutDashboard,
  Settings2,
  Variable,
  Users,
  Shield,
  Info,
  Lock,
} from "lucide-react";

const iconMap = {
  project: Folder,
  milestones: Flag,
  test_suites: ListChecks,
  attachments: Paperclip,
  test_runs: PlayCircle,
  reports: BarChart3,
  dashboard: LayoutDashboard,
  client_configuration: Settings2,
  variables: Variable,
  users: Users,
  roles: Shield,
};
// Permission descriptions
const permissionDescriptions = {
  project: "Manage project settings, lifecycle, and access.",
  test_suits:
    "Milestones used to track testing phases, releases, or delivery checkpoints.",
  test_suits_cases_sections: "Manage test cases and their hierarchy.",
  attachments: "Upload and manage linked files.",
  runs_plan: "Create and track test executions.",
  template: "Manage test case templates and Case, Result level Fields.",
  report: "View execution and coverage reports.",
  dashboard: "View project insights and metrics.",
  configuration: "Manage project-level configurations.",
  variables: "Manage reusable test variables.",
  user: "Manage users and project access.",
  role: "Manage roles and permissions.",
  defect_management: "View and manage defects, link to test cases and runs.",
  integration_management:
    "View and configure integrations (Jira, Slack, Figma).",
};

// Allowed features
const allowedFeatures = [
  "project",
  "test_suits",
  "test_suits_cases_sections",
  "attachments",
  "runs_plan",
  "template",
  "report",
  "dashboard",
  "configuration",
  "variables",
  "user",
  "role",
  "defect_management",
  "integration_management",
];

// Feature action mapping
const featureActionMap = {
  project: ["view", "add_edit", "inactive", "permanently_delete", "close"],
  test_suits: ["view", "add_edit", "permanently_delete"],
  test_suits_cases_sections: [
    "view",
    "add_edit",
    "inactive",
    "permanently_delete",
  ],
  attachments: ["view", "add_edit", "permanently_delete"],
  runs_plan: ["view", "add_edit", "inactive", "permanently_delete", "close"],
  template: ["view", "add_edit", "permanently_delete"],
  report: ["view", "add_edit", "inactive"],
  dashboard: ["view", "add_edit"],
  configuration: ["view", "add_edit"],
  variables: ["view", "add_edit", "permanently_delete"],
  user: ["view", "add_edit", "permanently_delete"],
  role: ["view", "add_edit", "permanently_delete"],
  defect_management: [
    "view",
    "add_edit",
    "inactive",
    "permanently_delete",
    "close",
  ],
  integration_management: ["view", "add_edit"],
};

function PermissionSwitch({ label, checked, disabled, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          opacity: disabled ? 0.4 : 0.9,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>

      <Switch
        checked={!!checked}
        disabled={!!disabled}
        onChange={onChange}
        size="small"
      />
    </Box>
  );
}

const ClientConfigurationStep = ({
  clientId,
  onSave,
  loading,
  saved,
  onSavedStateChange,
  disabled = false,
}) => {
  const [permissions, setPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [savedPermissions, setSavedPermissions] = useState(null);

  // Load available permissions
  const loadPermissions = useCallback(async () => {
    try {
      setPermissionsLoading(true);
      const response = await rbacService.getPermissionsGrouped();
      if (response.success && response.data) {
        // Response format from getPermissionsGrouped: { feature: [permission objects] }
        // Store permissions by feature
        const permsByFeature = {};
        Object.keys(response.data).forEach((feature) => {
          if (
            allowedFeatures.includes(feature) &&
            Array.isArray(response.data[feature])
          ) {
            permsByFeature[feature] = response.data[feature];
          }
        });
        setPermissions(permsByFeature);

        // Initialize all permissions to true (default for CLIENT_CONFIGURATION)
        const defaultPerms = {};
        allowedFeatures.forEach((feature) => {
          defaultPerms[feature] = {};
          const allowedActions = featureActionMap[feature] || [];
          allowedActions.forEach((action) => {
            defaultPerms[feature][action] = true; // Default all to true for CLIENT_CONFIGURATION
          });
        });
        setRolePermissions(defaultPerms);
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      toast.error("Failed to load permissions");
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load permissions even without clientId (for wizard flow)
    loadPermissions();
  }, [loadPermissions]);

  // Watch for permission changes after saving
  useEffect(() => {
    if (saved && savedPermissions) {
      const permissionsChanged =
        JSON.stringify(rolePermissions) !== JSON.stringify(savedPermissions);
      if (permissionsChanged && onSavedStateChange) {
        onSavedStateChange(false);
      }
    }
  }, [rolePermissions, saved, savedPermissions, onSavedStateChange]);

  // Handle permission toggle
  const handlePermissionToggle = (feature, action, currentGranted) => {
    setRolePermissions((prev) => {
      const updated = { ...prev };
      if (!updated[feature]) {
        updated[feature] = {};
      }
      updated[feature] = { ...updated[feature] };

      const newValue = !currentGranted;
      updated[feature][action] = newValue;

      // If view is being disabled, disable all other actions for this feature
      if (action === "view" && !newValue) {
        updated[feature]["add_edit"] = false;
        updated[feature]["inactive"] = false;
        updated[feature]["permanently_delete"] = false;
        updated[feature]["close"] = false;
      }

      return updated;
    });
  };

  // Get permission status
  const getPermissionStatus = (feature, action) => {
    if (
      rolePermissions[feature] &&
      rolePermissions[feature][action] !== undefined
    ) {
      return rolePermissions[feature][action] === true;
    }
    return false;
  };

  // Handle save
  const handleSave = () => {
    // Transform to module-based format
    const moduleMapping = {
      project: "Project Management Module",
      test_suits: "Test Management Module",
      test_suits_cases_sections: "Test Management Module",
      attachments: "Test Management Module",
      runs_plan: "Test Execution Module",
      template: "Test Management Module",
      report: "Reporting Module",
      dashboard: "Dashboard Module",
      configuration: "Configuration Module",
      variables: "Configuration Module",
      user: "User Management Module",
      role: "User Management Module",
    };

    const permissionsByModule = {};

    allowedFeatures.forEach((feature) => {
      const moduleName = moduleMapping[feature] || "Other Module";
      if (!permissionsByModule[moduleName]) {
        permissionsByModule[moduleName] = {};
      }

      const featurePerms = rolePermissions[feature] || {};
      const allowedActions = featureActionMap[feature] || [];

      // Get permission metadata
      const featurePermsFromState = permissions[feature] || [];
      const viewPerm = Array.isArray(featurePermsFromState)
        ? featurePermsFromState.find((p) => p.action === "view")
        : featurePermsFromState;

      // Build permission object
      const permObj = {
        levels: ["company"],
        meta_data: {
          description:
            (
              viewPerm?.description ||
              permissionDescriptions[feature] ||
              ""
            ).trim() || "-",
          name: (getFeatureDisplayName(feature) || "").trim() || "-",
        },
      };

      // Set all action flags explicitly
      if (allowedActions.includes("view")) {
        permObj.read = featurePerms.view === true;
      }
      if (allowedActions.includes("add_edit")) {
        permObj.write = featurePerms.add_edit === true;
      }
      if (allowedActions.includes("inactive")) {
        permObj.inactive = featurePerms.inactive === true;
      }
      if (allowedActions.includes("permanently_delete")) {
        permObj.permanently_delete = featurePerms.permanently_delete === true;
      }
      if (allowedActions.includes("close")) {
        permObj.close = featurePerms.close === true;
      }

      permissionsByModule[moduleName][feature] = permObj;
    });

    // Save current permissions state for comparison
    setSavedPermissions(JSON.parse(JSON.stringify(rolePermissions)));

    if (onSave) {
      onSave(permissionsByModule);
    }
  };

  // Get feature display name
  const getFeatureDisplayName = (feature) => {
    const mapping = {
      project: "Project",
      test_suits: "Milestones",
      test_suits_cases_sections: "Test Suites, Sections, Cases",
      attachments: "Attachments",
      runs_plan: "Test Runs/Plans",
      template: "Test Case / Test Run Layout",
      report: "Reports",
      dashboard: "Dashboard",
      configuration: "Client Configuration",
      variables: "Variables",
      user: "Users",
      role: "Roles",
    };
    return (
      mapping[feature] ||
      feature.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  // Get feature permissions
  const getFeaturePermissions = (feature) => {
    // Permissions structure from getPermissionsGrouped is: { feature: [permission objects] }
    const featurePerms = Array.isArray(permissions[feature])
      ? permissions[feature]
      : [];
    const allowedActions = featureActionMap[feature] || [];

    return {
      view: allowedActions.includes("view")
        ? featurePerms.find((p) => p.action === "view")
        : null,
      addEdit: allowedActions.includes("add_edit")
        ? featurePerms.find((p) => p.action === "add_edit")
        : null,
      inactive: allowedActions.includes("inactive")
        ? featurePerms.find((p) => p.action === "inactive")
        : null,
      permanentlyDelete: allowedActions.includes("permanently_delete")
        ? featurePerms.find((p) => p.action === "permanently_delete")
        : null,
      close: allowedActions.includes("close")
        ? featurePerms.find((p) => p.action === "close")
        : null,
    };
  };

  const features = allowedFeatures.filter(
    (feature) =>
      feature !== "other_permission" && feature !== "general_permission",
  );

  return (
    <div className="space-y-6">
      {permissionsLoading ? (
        <div className="py-2">
          <TableSkeleton rows={8} columns={5} />
        </div>
      ) : (
        <Card className="p-6">
          <div className="overflow-x-auto">
            <Box sx={{ width: "100%" }}>
              {features.map((feature, idx) => {
                const { view, addEdit, inactive, permanentlyDelete, close } =
                  getFeaturePermissions(feature);

                const featureDisplayName = getFeatureDisplayName(feature);
                const description = permissionDescriptions?.[feature] || "";

                const Icon = iconMap?.[feature] || Folder;

                const viewChecked = getPermissionStatus(feature, "view");

                return (
                  <Box key={feature}>
                    {/* Row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2,
                        py: 1.6,
                        borderRadius: "14px",
                        background: "#ECE9E9",
                        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
                      }}
                    >
                      {/* Left: icon + title + desc */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.4 }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "12px",
                            display: "grid",
                            placeItems: "center",
                            background: "#DFF3EA",
                          }}
                        >
                          <Icon size={20} />
                        </Box>

                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                              {featureDisplayName}
                            </Typography>
                            <Lock size={14} opacity={0.65} />
                          </Box>

                          <Typography sx={{ fontSize: 12.5, opacity: 0.7 }}>
                            {description || "—"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Right: switches */}
                      <Stack direction="row" spacing={2.2} alignItems="center">
                        <PermissionSwitch
                          label="View"
                          checked={viewChecked}
                          disabled={disabled || loading || !view}
                          onChange={() =>
                            handlePermissionToggle(
                              feature,
                              "view",
                              getPermissionStatus(feature, "view"),
                            )
                          }
                        />

                        <PermissionSwitch
                          label="Add/Edit"
                          checked={getPermissionStatus(feature, "add_edit")}
                          disabled={
                            disabled || loading || !viewChecked || !addEdit
                          }
                          onChange={() =>
                            handlePermissionToggle(
                              feature,
                              "add_edit",
                              getPermissionStatus(feature, "add_edit"),
                            )
                          }
                        />

                        <PermissionSwitch
                          label="Inactive"
                          checked={getPermissionStatus(feature, "inactive")}
                          disabled={
                            disabled || loading || !viewChecked || !inactive
                          }
                          onChange={() =>
                            handlePermissionToggle(
                              feature,
                              "inactive",
                              getPermissionStatus(feature, "inactive"),
                            )
                          }
                        />

                        <PermissionSwitch
                          label="Permanently Delete"
                          checked={getPermissionStatus(
                            feature,
                            "permanently_delete",
                          )}
                          disabled={
                            disabled ||
                            loading ||
                            !viewChecked ||
                            !permanentlyDelete
                          }
                          onChange={() =>
                            handlePermissionToggle(
                              feature,
                              "permanently_delete",
                              getPermissionStatus(
                                feature,
                                "permanently_delete",
                              ),
                            )
                          }
                        />

                        <PermissionSwitch
                          label="Close"
                          checked={getPermissionStatus(feature, "close")}
                          disabled={
                            disabled || loading || !viewChecked || !close
                          }
                          onChange={() =>
                            handlePermissionToggle(
                              feature,
                              "close",
                              getPermissionStatus(feature, "close"),
                            )
                          }
                        />
                      </Stack>
                    </Box>

                    {/* spacing */}
                    {idx !== features.length - 1 && <Box sx={{ height: 10 }} />}
                  </Box>
                );
              })}
            </Box>
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3">
            {saved &&
              savedPermissions &&
              JSON.stringify(rolePermissions) ===
                JSON.stringify(savedPermissions) && (
                <div className="flex items-center text-green-600 text-sm">
                  <Check className="w-4 h-4 mr-2" />
                  Configuration saved
                </div>
              )}
            <Button
              variant="main"
              style={{
                background: "#2E9E6F",
                color: "white",
              }}
              onClick={handleSave}
              disabled={
                loading ||
                permissionsLoading ||
                (saved &&
                  savedPermissions &&
                  JSON.stringify(rolePermissions) ===
                    JSON.stringify(savedPermissions))
              }
            >
              {loading ? (
                <>Saving...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ClientConfigurationStep;
